/**
 * Cross-device tracking (#166) — operator UI.
 *
 * Identity-resolved cross-device attribution. The dash surface lets an
 * operator:
 *   - See a KPI strip: total identities, linked devices, cross-device
 *     identities (>=2 distinct device kinds), attributable revenue.
 *   - Resolve / simulate an identity: enter a device_id + a deterministic
 *     identifier (email or user_id). The API upserts the identity and
 *     either creates a new link, reuses, or rebinds as needed.
 *   - Inspect identities: recent list sorted by last_seen, one-click
 *     detail drill-down showing all linked devices + recent events.
 *
 * Brand: emerald->sky headline gradient, zinc borders, 6-8px rounded,
 * light theme only, no emojis.
 *
 * Data layer lives in `./api`; this file is render + wiring.
 */
import { useCallback, useMemo, useState } from "react";
import {
	useMutation,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query";
import { toast } from "sonner";
import {
	countIdentitiesByIdentityKind,
	crossDeviceQueryKeys,
	DEVICE_KIND_LABELS,
	DEVICE_KINDS,
	EMPTY_RESOLVE_DRAFT,
	IDENTITY_KIND_LABELS,
	IDENTITY_KINDS,
	deviceKindBadgeClass,
	distinctDeviceKinds,
	formatMoney,
	formatTimestamp,
	getIdentity,
	getSummary,
	listIdentities,
	resolveDraftSchema,
	resolveIdentity,
	sortEvents,
	sortIdentities,
	sortLinks,
	truncateKey,
	type CrossDeviceSummary,
	type DeviceKind,
	type IdentityDetailResponse,
	type IdentityKind,
	type IdentityRow,
	type ResolveDraft,
	type ResolveResponse,
} from "./api";

// ---------------------------------------------------------------------------
// Style tokens — mirror the dash brand rules.
// ---------------------------------------------------------------------------

const CARD =
	"rounded-[8px] border border-zinc-200 bg-white shadow-[0_1px_3px_0_rgb(24_24_27/0.08),0_4px_8px_-2px_rgb(24_24_27/0.06)]";
const LABEL = "text-sm font-medium text-zinc-700";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20";
const SELECT = INPUT;
const BTN_PRIMARY =
	"rounded-[6px] border border-emerald-600 bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white shadow-[0_1px_2px_0_rgb(24_24_27/0.06)] hover:bg-emerald-700 hover:border-emerald-700 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_NEUTRAL =
	"rounded-[6px] border border-zinc-300 bg-white px-3 py-1.5 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60";
const PILL =
	"inline-flex items-center gap-1 rounded-[6px] border px-2 py-0.5 text-xs font-semibold";
const BADGE_CROSS =
	"inline-flex items-center gap-1 rounded-[6px] border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-700";
const BADGE_SOLO =
	"inline-flex items-center gap-1 rounded-[6px] border border-zinc-200 bg-zinc-50 px-2 py-0.5 text-xs font-semibold text-zinc-600";

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function CrossDevicePage() {
	const queryClient = useQueryClient();

	const [draft, setDraft] = useState<ResolveDraft>(EMPTY_RESOLVE_DRAFT);
	const [errors, setErrors] = useState<Record<string, string>>({});
	const [selectedIdentityId, setSelectedIdentityId] = useState<string | null>(
		null,
	);

	// ---- Queries -----------------------------------------------------------
	const summaryQuery = useQuery<CrossDeviceSummary | null>({
		queryKey: crossDeviceQueryKeys.summary(),
		queryFn: () => getSummary(),
	});

	const LIST_LIMIT = 50;
	const identitiesQuery = useQuery<IdentityRow[]>({
		queryKey: crossDeviceQueryKeys.identities(LIST_LIMIT),
		queryFn: () => listIdentities(LIST_LIMIT),
	});

	const identityDetailQuery = useQuery<IdentityDetailResponse | null>({
		queryKey: crossDeviceQueryKeys.identity(selectedIdentityId ?? ""),
		queryFn: () =>
			selectedIdentityId ? getIdentity(selectedIdentityId) : Promise.resolve(null),
		enabled: selectedIdentityId !== null,
	});

	const identities = useMemo<IdentityRow[]>(
		() => sortIdentities(identitiesQuery.data ?? []),
		[identitiesQuery.data],
	);
	const summary = summaryQuery.data ?? null;
	const identityCounts = useMemo(
		() => countIdentitiesByIdentityKind(identities),
		[identities],
	);

	// ---- Mutations ---------------------------------------------------------
	const resolveMutation = useMutation<
		ResolveResponse,
		Error,
		ResolveDraft
	>({
		mutationFn: async (input) => {
			const parsed = resolveDraftSchema.safeParse(input);
			if (!parsed.success) {
				setErrors(parsed.errors);
				throw new Error("Please fix the highlighted fields.");
			}
			setErrors({});
			return resolveIdentity(parsed.data);
		},
		onSuccess: (res) => {
			const parts: string[] = [];
			if (res.created_identity) parts.push("new identity");
			if (res.created_link) parts.push("device linked");
			if (res.updated_link && !res.created_link) parts.push("device updated");
			if (res.rebound) parts.push("rebound");
			toast.success(
				parts.length === 0
					? "Resolved."
					: `Resolved — ${parts.join(", ")}.`,
			);
			queryClient.invalidateQueries({ queryKey: crossDeviceQueryKeys.all });
			if (res.identity_id) setSelectedIdentityId(res.identity_id);
		},
		onError: (err) => {
			toast.error(err.message || "Resolve failed.");
		},
	});

	const busy = resolveMutation.isPending;

	// ---- Handlers ----------------------------------------------------------
	const handleField = useCallback(
		<K extends keyof ResolveDraft>(field: K, value: ResolveDraft[K]) => {
			setDraft((prev) => ({ ...prev, [field]: value }));
			setErrors((prev) => {
				if (!prev[field as string]) return prev;
				const { [field as string]: _dropped, ...rest } = prev;
				return rest;
			});
		},
		[],
	);

	const handleSubmit = useCallback(
		(e: React.FormEvent<HTMLFormElement>) => {
			e.preventDefault();
			if (busy) return;
			resolveMutation.mutate(draft);
		},
		[busy, draft, resolveMutation],
	);

	const handleReset = useCallback(() => {
		setDraft(EMPTY_RESOLVE_DRAFT);
		setErrors({});
	}, []);

	const handleSelect = useCallback((id: string) => {
		setSelectedIdentityId(id);
	}, []);

	// -----------------------------------------------------------------------
	// Render
	// -----------------------------------------------------------------------
	return (
		<main
			data-testid="cross-device-page"
			className="mx-auto flex w-full max-w-5xl flex-col gap-8 px-6 py-10 sm:py-14"
		>
			<Header />

			<KpiStrip
				summary={summary}
				identityCounts={identityCounts}
				loading={summaryQuery.isLoading}
			/>

			<ResolverCard
				draft={draft}
				errors={errors}
				busy={busy}
				onField={handleField}
				onSubmit={handleSubmit}
				onReset={handleReset}
			/>

			<div className="grid grid-cols-1 gap-6 lg:grid-cols-5">
				<div className="lg:col-span-2">
					<IdentitiesCard
						identities={identities}
						loading={identitiesQuery.isLoading}
						error={identitiesQuery.error?.message ?? null}
						selectedId={selectedIdentityId}
						onSelect={handleSelect}
					/>
				</div>
				<div className="lg:col-span-3">
					<IdentityDetailCard
						selectedId={selectedIdentityId}
						data={identityDetailQuery.data ?? null}
						loading={identityDetailQuery.isLoading}
						error={identityDetailQuery.error?.message ?? null}
					/>
				</div>
			</div>
		</main>
	);
}

export default CrossDevicePage;

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function Header() {
	return (
		<header className="flex flex-col gap-2">
			<p className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-500">
				Tracking
			</p>
			<h1 className="text-brand-gradient text-3xl font-semibold tracking-tight sm:text-4xl">
				Cross-device tracking
			</h1>
			<p className="max-w-prose text-sm text-zinc-600">
				Deterministic attribution across desktop, tablet, and mobile. Each
				login or first-party call ties an opaque device fingerprint to a
				stable identity (hashed email or user id), so later sessions on
				any other device resolve to the same journey.
			</p>
		</header>
	);
}

function KpiStrip(props: {
	summary: CrossDeviceSummary | null;
	identityCounts: Record<IdentityKind, number>;
	loading: boolean;
}) {
	const s = props.summary;
	const cells: Array<{ label: string; value: string; hint?: string }> = [
		{
			label: "Identities",
			value: s ? s.identities_total.toLocaleString("en-US") : "—",
			hint:
				props.identityCounts.email || props.identityCounts.user_id
					? `${props.identityCounts.email} email · ${props.identityCounts.user_id} user id`
					: undefined,
		},
		{
			label: "Linked devices",
			value: s ? s.links_total.toLocaleString("en-US") : "—",
			hint: s
				? `${s.by_device_kind.desktop} desktop · ${s.by_device_kind.tablet} tablet · ${s.by_device_kind.mobile} mobile`
				: undefined,
		},
		{
			label: "Cross-device",
			value: s ? s.cross_device_identities.toLocaleString("en-US") : "—",
			hint: "Identities on 2+ device kinds",
		},
		{
			label: "Attributed revenue",
			value: s ? formatMoney(s.value_cents_total) : "—",
			hint: s && s.events_total > 0 ? `${s.events_total} events` : undefined,
		},
	];
	return (
		<section
			aria-label="Cross-device statistics"
			className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4"
			data-testid="kpi-strip"
		>
			{cells.map((c) => (
				<div key={c.label} className={`${CARD} flex flex-col gap-1 p-4`}>
					<p className="text-xs font-medium uppercase tracking-wide text-zinc-500">
						{c.label}
					</p>
					<p className="text-2xl font-semibold tabular-nums text-zinc-900">
						{props.loading ? "—" : c.value}
					</p>
					{c.hint ? (
						<p className="text-xs text-zinc-500">{c.hint}</p>
					) : null}
				</div>
			))}
		</section>
	);
}

function ResolverCard(props: {
	draft: ResolveDraft;
	errors: Record<string, string>;
	busy: boolean;
	onField: <K extends keyof ResolveDraft>(field: K, value: ResolveDraft[K]) => void;
	onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
	onReset: () => void;
}) {
	return (
		<section className={`${CARD} flex flex-col`} data-testid="resolver-card">
			<header className="flex flex-col gap-1 border-b border-zinc-200 p-5">
				<h2 className="text-base font-semibold text-zinc-900">
					Resolve identity
				</h2>
				<p className="text-sm text-zinc-500">
					Simulate an edge call. The API deterministically hashes the
					identifier and binds it to the device. Subsequent calls from the
					same device resolve to the same identity without the identifier.
				</p>
			</header>

			<form
				onSubmit={props.onSubmit}
				className="flex flex-col gap-4 p-5"
				data-testid="resolver-form"
				noValidate
			>
				<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
					<Field label="Device ID" htmlFor="xd-device-id" error={props.errors.deviceId}>
						<input
							id="xd-device-id"
							name="device_id"
							type="text"
							autoComplete="off"
							className={INPUT}
							placeholder="dev_browser_123"
							value={props.draft.deviceId}
							onChange={(e) => props.onField("deviceId", e.target.value)}
							aria-invalid={Boolean(props.errors.deviceId) || undefined}
						/>
					</Field>
					<Field label="Device kind" htmlFor="xd-device-kind" error={props.errors.deviceKind}>
						<select
							id="xd-device-kind"
							name="device_kind"
							className={SELECT}
							value={props.draft.deviceKind}
							onChange={(e) =>
								props.onField("deviceKind", e.target.value as DeviceKind)
							}
						>
							{DEVICE_KINDS.map((k) => (
								<option key={k} value={k}>
									{DEVICE_KIND_LABELS[k]}
								</option>
							))}
						</select>
					</Field>
					<Field label="Identifier kind" htmlFor="xd-id-kind" error={props.errors.identityKind}>
						<select
							id="xd-id-kind"
							name="identity_kind"
							className={SELECT}
							value={props.draft.identityKind}
							onChange={(e) =>
								props.onField("identityKind", e.target.value as IdentityKind)
							}
						>
							{IDENTITY_KINDS.map((k) => (
								<option key={k} value={k}>
									{IDENTITY_KIND_LABELS[k]}
								</option>
							))}
						</select>
					</Field>
					<Field label="Identifier value" htmlFor="xd-id-value" error={props.errors.identityValue}>
						<input
							id="xd-id-value"
							name="identity_value"
							type="text"
							autoComplete="off"
							className={INPUT}
							placeholder={
								props.draft.identityKind === "email"
									? "user@example.com"
									: "cust_42"
							}
							value={props.draft.identityValue}
							onChange={(e) => props.onField("identityValue", e.target.value)}
							aria-invalid={Boolean(props.errors.identityValue) || undefined}
						/>
					</Field>
				</div>

				<div className="flex items-center justify-end gap-2">
					<button
						type="button"
						className={BTN_NEUTRAL}
						onClick={props.onReset}
						disabled={props.busy}
					>
						Reset
					</button>
					<button
						type="submit"
						className={BTN_PRIMARY}
						disabled={props.busy}
						data-testid="resolve-submit"
					>
						{props.busy ? "Resolving." : "Resolve"}
					</button>
				</div>
			</form>
		</section>
	);
}

function Field(props: {
	label: string;
	htmlFor: string;
	error?: string;
	children: React.ReactNode;
}) {
	return (
		<label className="flex flex-col gap-1" htmlFor={props.htmlFor}>
			<span className={LABEL}>{props.label}</span>
			{props.children}
			{props.error ? (
				<span className="text-xs text-red-600" role="alert">
					{props.error}
				</span>
			) : null}
		</label>
	);
}

function IdentitiesCard(props: {
	identities: readonly IdentityRow[];
	loading: boolean;
	error: string | null;
	selectedId: string | null;
	onSelect: (id: string) => void;
}) {
	return (
		<section className={`${CARD} flex h-full flex-col`} data-testid="identities-card">
			<header className="flex flex-col gap-1 border-b border-zinc-200 p-5">
				<h2 className="text-base font-semibold text-zinc-900">
					Recent identities
				</h2>
				<p className="text-sm text-zinc-500">
					Most-recently-seen identities for this tenant. Click to inspect.
				</p>
			</header>
			{props.error ? (
				<div className="p-5 text-sm text-red-600" role="alert">
					{props.error}
				</div>
			) : props.loading ? (
				<div className="p-5 text-sm text-zinc-500">Loading identities.</div>
			) : props.identities.length === 0 ? (
				<div className="p-5 text-sm text-zinc-600">
					No identities yet. Use the resolver above to bind the first one.
				</div>
			) : (
				<ul
					className="divide-y divide-zinc-200"
					aria-label="Identities"
					data-testid="identities-list"
				>
					{props.identities.map((row) => {
						const active = props.selectedId === row.id;
						return (
							<li key={row.id}>
								<button
									type="button"
									onClick={() => props.onSelect(row.id)}
									className={`flex w-full items-center justify-between gap-3 px-5 py-3 text-left transition hover:bg-zinc-50 ${active ? "bg-emerald-50/60" : ""}`}
									data-testid={`identity-row-${row.id}`}
									aria-pressed={active}
								>
									<div className="flex flex-col gap-0.5 min-w-0">
										<span className="truncate font-mono text-xs text-zinc-900">
											{row.id}
										</span>
										<span className="truncate text-xs text-zinc-500">
											{IDENTITY_KIND_LABELS[row.identity_kind]} ·{" "}
											<span className="font-mono">
												{truncateKey(row.identity_key)}
											</span>
										</span>
									</div>
									<span className="shrink-0 text-xs tabular-nums text-zinc-500">
										{formatTimestamp(row.last_seen_at)}
									</span>
								</button>
							</li>
						);
					})}
				</ul>
			)}
		</section>
	);
}

function IdentityDetailCard(props: {
	selectedId: string | null;
	data: IdentityDetailResponse | null;
	loading: boolean;
	error: string | null;
}) {
	if (props.selectedId === null) {
		return (
			<section className={`${CARD} flex h-full flex-col`} data-testid="identity-detail-card">
				<header className="border-b border-zinc-200 p-5">
					<h2 className="text-base font-semibold text-zinc-900">
						Identity detail
					</h2>
					<p className="text-sm text-zinc-500">
						Pick an identity on the left to see its linked devices and
						recent events.
					</p>
				</header>
				<div className="p-5 text-sm text-zinc-500">Nothing selected.</div>
			</section>
		);
	}

	if (props.error) {
		return (
			<section className={`${CARD} flex h-full flex-col`} data-testid="identity-detail-card">
				<header className="border-b border-zinc-200 p-5">
					<h2 className="text-base font-semibold text-zinc-900">
						Identity detail
					</h2>
				</header>
				<div className="p-5 text-sm text-red-600" role="alert">
					{props.error}
				</div>
			</section>
		);
	}

	if (props.loading || props.data === null) {
		return (
			<section className={`${CARD} flex h-full flex-col`} data-testid="identity-detail-card">
				<header className="border-b border-zinc-200 p-5">
					<h2 className="text-base font-semibold text-zinc-900">
						Identity detail
					</h2>
				</header>
				<div className="p-5 text-sm text-zinc-500">
					{props.loading ? "Loading." : "Identity not found."}
				</div>
			</section>
		);
	}

	const { identity, links, events } = props.data;
	const sortedLinks = sortLinks(links);
	const sortedEvents = sortEvents(events);
	const kinds = distinctDeviceKinds(links);
	const crossDevice = kinds.length >= 2;

	return (
		<section className={`${CARD} flex h-full flex-col`} data-testid="identity-detail-card">
			<header className="flex flex-col gap-3 border-b border-zinc-200 p-5">
				<div className="flex flex-wrap items-start justify-between gap-3">
					<div>
						<h2 className="text-base font-semibold text-zinc-900">
							{IDENTITY_KIND_LABELS[identity.identity_kind]} identity
						</h2>
						<p className="font-mono text-xs text-zinc-500">
							{identity.id} · {truncateKey(identity.identity_key)}
						</p>
					</div>
					<span className={crossDevice ? BADGE_CROSS : BADGE_SOLO}>
						{crossDevice ? `Cross-device (${kinds.length} kinds)` : "Single-device"}
					</span>
				</div>
				<dl className="grid grid-cols-2 gap-2 text-xs text-zinc-600">
					<div>
						<dt className="font-medium text-zinc-500">First seen</dt>
						<dd className="tabular-nums">{formatTimestamp(identity.created_at)}</dd>
					</div>
					<div>
						<dt className="font-medium text-zinc-500">Last seen</dt>
						<dd className="tabular-nums">{formatTimestamp(identity.last_seen_at)}</dd>
					</div>
				</dl>
			</header>

			<div className="flex flex-col gap-3 border-b border-zinc-200 p-5">
				<h3 className="text-sm font-semibold text-zinc-900">Linked devices</h3>
				{sortedLinks.length === 0 ? (
					<p className="text-sm text-zinc-500">No devices linked yet.</p>
				) : (
					<ul className="flex flex-col gap-2" data-testid="links-list">
						{sortedLinks.map((link) => (
							<li
								key={link.id}
								className="flex items-center justify-between gap-3 rounded-[6px] border border-zinc-200 bg-white px-3 py-2"
							>
								<div className="flex min-w-0 items-center gap-2">
									<span className={`${PILL} ${deviceKindBadgeClass(link.device_kind)}`}>
										{DEVICE_KIND_LABELS[link.device_kind]}
									</span>
									<span className="truncate font-mono text-xs text-zinc-700">
										{link.device_id}
									</span>
								</div>
								<span className="shrink-0 text-xs tabular-nums text-zinc-500">
									{formatTimestamp(link.last_seen_at)}
								</span>
							</li>
						))}
					</ul>
				)}
			</div>

			<div className="flex flex-col gap-3 p-5">
				<h3 className="text-sm font-semibold text-zinc-900">Recent events</h3>
				{sortedEvents.length === 0 ? (
					<p className="text-sm text-zinc-500">No events recorded.</p>
				) : (
					<ul className="flex flex-col gap-2" data-testid="events-list">
						{sortedEvents.slice(0, 20).map((ev) => (
							<li
								key={ev.id}
								className="flex items-center justify-between gap-3 rounded-[6px] border border-zinc-200 bg-white px-3 py-2"
							>
								<div className="flex min-w-0 items-center gap-2">
									<span className={`${PILL} ${deviceKindBadgeClass(ev.device_kind as DeviceKind)}`}>
										{DEVICE_KIND_LABELS[ev.device_kind as DeviceKind]}
									</span>
									<span className="truncate text-sm text-zinc-900">
										{ev.event_type}
									</span>
								</div>
								<div className="flex shrink-0 items-center gap-3 text-xs tabular-nums text-zinc-500">
									{ev.value_cents > 0 ? (
										<span className="font-medium text-zinc-900">
											{formatMoney(ev.value_cents)}
										</span>
									) : null}
									<span>{formatTimestamp(ev.occurred_at)}</span>
								</div>
							</li>
						))}
					</ul>
				)}
			</div>
		</section>
	);
}
